import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, InputField, Modal, ModalContent, ModalFooter } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { PortalUserDocument, RevokeMembershipDocument } from '../../graphql/__generated__/types';
import { revokeRefusalOf } from './revokeRefusal';

interface Props {
    membershipId: string;
    organizationName: string;
    roleName: string;
    personName: string;
    onClose: () => void;
}

/**
 * Closing is not deleting: the membership goes to « closed » status, keeps its history, and can
 * be reopened later (reinstateMembership). Closing a person's last open access cancels their
 * pending invitation, the one effect that spills beyond the account acted on. The reason is
 * optional but the only trace of why the access was cut.
 */
const RevokeMembershipDialog = ({ membershipId, organizationName, roleName, personName, onClose }: Props) => {
    const [reason, setReason] = useState('');

    const [revoke, { data, loading, error }] = useMutation(RevokeMembershipDocument, {
        refetchQueries: [PortalUserDocument],
        awaitRefetchQueries: true,
    });

    const submit = () => {
        const trimmed = reason.trim();
        revoke({ variables: { membershipId, reason: trimmed.length > 0 ? trimmed : null } }).catch(() => {});
    };

    /* ------------------------------------------------------------------- done */
    if (data?.revokeMembership) {
        return (
            <Modal open onClose={onClose} title={c('Revoke access').t`Access closed`}>
                <ModalContent>
                    <Alert type="success" title={c('Revoke access').t`${organizationName} is closed to this person.`}>
                        <p className="text-sm">
                            {c('Revoke access')
                                .t`Their access to this account is closed. Nothing is deleted: the closure is on record with its date and reason, and it can be reopened later from this sheet.`}
                        </p>
                    </Alert>
                </ModalContent>
                <ModalFooter>
                    <Button category="default" onClick={onClose}>
                        {c('Action').t`Close`}
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }

    /* ---------------------------------------------------------------- confirm */
    return (
        <Modal open onClose={onClose} title={c('Revoke access').t`Close this access`}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-900">
                        <b>{personName}</b>
                        <span className="text-text-600">
                            {' '}
                            · {organizationName} · {roleName}
                        </span>
                    </p>

                    <Alert type="warning">
                        {c('Revoke access')
                            .t`Closing removes this person's access to ${organizationName}. It is not a deletion: the closure keeps its history and can be reopened later. If this is their last open access, the pending invitation is cancelled and their next request is refused.`}
                    </Alert>

                    <InputField
                        id="revoke-reason"
                        label={c('Revoke access').t`Reason (optional)`}
                        type="text"
                        value={reason}
                        onValue={setReason}
                        placeholder={c('Revoke access').t`Left the company, access no longer needed…`}
                    />
                    <p className="text-xs text-text-600">
                        {c('Revoke access')
                            .t`It is journalled and shown when someone considers reopening the access. Leave it empty if there is nothing to add.`}
                    </p>

                    {error && <RefusalNotice refusal={revokeRefusalOf(error)} />}
                </div>
            </ModalContent>
            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={loading}>
                    {c('Action').t`Cancel`}
                </Button>
                <Button category="destructive" onClick={submit} loading={loading}>
                    {c('Revoke access').t`Close the access`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default RevokeMembershipDialog;
